// accountIdentity — what the left menu's account row shows, pure (pen board
// 32 "Left menu – settings and account", decided 27 Oct).
//
// The name is the author's display name when they set one, else the part of
// their email before the @; the email itself is never shown in the menu (it
// is in Settings). Nothing usable in either: the default case, "Account"
// with a generic avatar. The initials come from the name: the first letters
// of its first two words ("Anna Lind" → "AL"), one letter for an email's
// local part ("anna.lind" → "A"), "?" when the name has no letter.
//
// A name is text, never markup: control characters and bidi overrides are
// dropped (the row also isolates it with <bdi>), and a very long name is
// clamped before it reaches the DOM; the row cuts it with an ellipsis.

export type { AccountIdentity };
export { accountIdentityOf };

type AccountIdentity =
    | {
          readonly type: "named";
          readonly name: string;
          readonly initials: string;
      }
    // No display name and no usable email.
    | { readonly type: "anonymous" };

// More than any row can show; the rest would only cost layout.
const NAME_MAX = 120;

// Control characters (C0, C1) and the bidi embedding / override / isolate
// marks: never part of a name as shown.
const UNSAFE = /[\p{Cc}\u200e\u200f\u202a-\u202e\u2066-\u2069]/gu;

const clean = (raw: string): string =>
    raw.replace(UNSAFE, "").trim().slice(0, NAME_MAX);

// The first letter (any script) of a word, upper-cased, or "" when none.
const firstLetter = (word: string): string =>
    (word.match(/\p{L}/u)?.[0] ?? "").toLocaleUpperCase();

const initialsOf = (name: string): string => {
    const initials = name.split(/\s+/u).slice(0, 2).map(firstLetter).join("");
    return initials === "" ? "?" : initials;
};

const accountIdentityOf = (
    displayName: string,
    email: string,
): AccountIdentity => {
    const named = clean(displayName);
    if (named !== "")
        return { type: "named", name: named, initials: initialsOf(named) };
    const local = clean(email.split("@")[0] ?? "");
    if (local !== "")
        return { type: "named", name: local, initials: initialsOf(local) };
    return { type: "anonymous" };
};
